import { createFileRoute, useBlocker } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { useCallback, useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { HTTPError } from "ky";
import { toast } from "sonner";
import * as z from "zod";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/http";
import { userInfoQueryOptions } from "@/lib/queries/auth";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { UnsavedChangesDialog } from "@/components/ui/confirmation-dialog";
import { requiredString } from "@/lib/utils/zod";

export const Route = createFileRoute("/_app/company/")({
  component: RouteComponent,
  async loader({ context: { queryClient } }) {
    await Promise.allSettled([
      queryClient.ensureQueryData(userInfoQueryOptions),
      queryClient.ensureQueryData(companiesQueryOptions()),
    ]);
  },
});

const updateCompanySchema = z.object({
  name: requiredString("Company name is required"),
  description: requiredString("Description is required"),
});

type UpdateCompany = z.infer<typeof updateCompanySchema>;

type Company = {
  id: string;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
};

function companiesQueryOptions() {
  return queryOptions({
    queryKey: ["organizations", "me"],
    queryFn: () => api.auth.raw.get("api/v1/organizations/me").json<Company>(),
  });
}

function RouteComponent() {
  const userInfo = useSuspenseQuery(userInfoQueryOptions);

  const isAdmin = userInfo.data.org_admin;

  const companiesQuery = useQuery(companiesQueryOptions());
  const queryClient = useQueryClient();

  const company = companiesQuery.data;

  const form = useForm<UpdateCompany>({
    resolver: zodResolver(updateCompanySchema),
    values: {
      name: company?.name ?? "",
      description: company?.description ?? "",
    },
  });

  const updateOrganization = useMutation({
    mutationFn: (data: UpdateCompany) => {
      return api.auth.raw
        .put(`api/v1/organizations/${companiesQuery.data?.id}`, {
          json: data,
        })
        .json<Company>();
    },
    onSuccess(data) {
      queryClient.setQueryData(["organizations", "me"], data);
      toast.success("Organization updated successfully");
    },
    async onError(error) {
      if (error instanceof HTTPError) {
        try {
          const body = await error.response.json();
          if (body.detail) {
            toast.error(body.detail);
            return;
          }
        } catch {
          // ignore json parse errors
        }
      }
      toast.error("Failed to update organization");
    },
  });

  const isDirty = form.formState.isDirty;

  const [unsavedDialogOpen, setUnsavedDialogOpen] = useState(false);
  const [isSavingAndLeaving, setIsSavingAndLeaving] = useState(false);
  const dismissedBlockerRef = useRef(false);

  const blocker = useBlocker({
    shouldBlockFn: () => isDirty,
    withResolver: true,
    enableBeforeUnload: isDirty,
  });

  useEffect(() => {
    if (blocker.status !== "blocked") {
      dismissedBlockerRef.current = false;
      return;
    }
    if (!unsavedDialogOpen && !dismissedBlockerRef.current) {
      setUnsavedDialogOpen(true);
    }
  }, [blocker.status, unsavedDialogOpen]);

  const handleKeepEditing = useCallback(() => {
    dismissedBlockerRef.current = true;
    if (blocker.status === "blocked") {
      blocker.reset();
    }
    setUnsavedDialogOpen(false);
  }, [blocker]);

  const handleDiscardAndLeave = useCallback(() => {
    form.reset();
    setUnsavedDialogOpen(false);
    if (blocker.status === "blocked") {
      blocker.proceed();
    }
  }, [blocker, form]);

  const handleSaveAndLeave = useCallback(async () => {
    if (!company) return;
    if (!isAdmin) {
      toast.error("You do not have permission to update the company.");
      return;
    }

    setIsSavingAndLeaving(true);
    try {
      await form.handleSubmit(
        async (values) => {
          await updateOrganization.mutateAsync(values);
          setUnsavedDialogOpen(false);
          if (blocker.status === "blocked") {
            blocker.proceed();
          }
        },
        () => {
          // Validation failed: stay on the page so the errors stay visible.
          handleKeepEditing();
        },
      )();
    } catch {
      setUnsavedDialogOpen(false);
      if (blocker.status === "blocked") {
        blocker.reset();
      }
    } finally {
      setIsSavingAndLeaving(false);
    }
  }, [blocker, company, form, handleKeepEditing, isAdmin, updateOrganization]);

  if (companiesQuery.isLoading) {
    return <div className="p-6">Loading company...</div>;
  }

  if (companiesQuery.isError) {
    return <div className="p-6 text-red-500">Failed to load company.</div>;
  }

  function onSubmit(values: UpdateCompany) {
    if (!company) return;
    if (!isAdmin) {
      toast.error("You do not have permission to update the company.");
      return;
    }

    updateOrganization.mutate(values);
  }

  return (
    <div className="w-full">
      {!company ? (
        <div>No company found.</div>
      ) : (
        <div>
          <div className="mb-8">
            <div className="mb-2 flex items-center gap-3">
              <h1 className="shrink-0 text-2xl leading-8 font-semibold text-[#181D27] dark:text-foreground">
                Company Information
              </h1>
            </div>
            <p className="text-muted-foreground">
              Manage your company details and settings.
            </p>
            {!isAdmin && (
              <p className="mt-2 text-sm text-muted-foreground">
                Only organization administrators can edit company information.
              </p>
            )}
          </div>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <Card>
                <CardHeader>
                  <CardDescription>Update your company details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company Name</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Enter company name"
                            readOnly={!isAdmin}
                            aria-readonly={!isAdmin}
                            className="read-only:cursor-default read-only:text-muted-foreground read-only:focus-visible:border-input read-only:focus-visible:ring-0"
                            disabled={updateOrganization.isPending}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            placeholder="Enter company description"
                            rows={4}
                            readOnly={!isAdmin}
                            aria-readonly={!isAdmin}
                            className="read-only:cursor-default read-only:text-muted-foreground read-only:focus-visible:border-input read-only:focus-visible:ring-0"
                            disabled={updateOrganization.isPending}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {isAdmin && (
                <div className="flex justify-end gap-3">
                  <Button
                    type="submit"
                    className="dark:bg-[#DD2590] dark:hover:bg-[#C4207D]"
                    disabled={
                      updateOrganization.isPending || !form.formState.isDirty
                    }
                  >
                    {updateOrganization.isPending
                      ? "Saving..."
                      : "Save Changes"}
                  </Button>
                </div>
              )}
            </form>
          </Form>
        </div>
      )}
      <UnsavedChangesDialog
        open={unsavedDialogOpen}
        onOpenChange={(open) => {
          if (!open) handleKeepEditing();
        }}
        onKeepEditing={handleKeepEditing}
        onDiscardAndLeave={handleDiscardAndLeave}
        onSaveAndLeave={handleSaveAndLeave}
        isSaving={isSavingAndLeaving}
      />
    </div>
  );
}
